import { Box, styled } from '@mui/material';
import { ChangeEvent } from 'react';

import { FormInput } from '@Components/Form';
import FormatNumberInput from '@Components/FormattedNumberInput';
import { disabledFormInputStyles } from '@Components/Form/styles';
import {
  OTHER_INCOME_STRUCTURE_ID,
  INCOME_SUBGROUP_CATEGORY,
  TOTAL_INCOME_STRUCTURE_ID,
} from 'src/Enum/IncomeStructure';
import {
  IIncomeItemFormValues,
  IIncomeStructureFormValues,
} from '../../NatureOfBusinessFormProvider/types';
import { useNatureOfBusinessForm } from '../../NatureOfBusinessFormProvider';
import {
  calcNetIncomeItem,
  sumExtendedValues,
  yearFields,
  calcIncomeItemPct,
} from '../utils';

const FormNumberInput = styled(FormInput)({
  '& input': {
    textAlign: 'right',
  },
});

type ProductIncomeYearFieldsFormProps = {
  keyItem: string;
};

const ProductIncomeYearFieldsForm = ({
  keyItem,
}: ProductIncomeYearFieldsFormProps) => {
  const { watch, setValue } = useNatureOfBusinessForm();

  const incomeStructure = watch(
    'incomeStructure'
  ) as IIncomeStructureFormValues;

  const productIncomeItems = incomeStructure?.productIncomeItems || {};
  const productIncomePercentages =
    incomeStructure?.productIncomePercentages || {};
  const { isCompletedTh = false, isCompletedEn = false } =
    incomeStructure?.completedSection ?? {};

  const handleChange = (
    event: ChangeEvent<HTMLInputElement>,
    yearField: keyof IIncomeItemFormValues
  ) => {
    const value = event.target.value;
    const totalValue = productIncomeItems[
      TOTAL_INCOME_STRUCTURE_ID.TOTAL_PRODUCT_INCOME
    ][yearField] as string;

    if (keyItem === TOTAL_INCOME_STRUCTURE_ID.TOTAL_PRODUCT_INCOME.toString()) {
      setValue(
        `incomeStructure.exportIncomeItems.${TOTAL_INCOME_STRUCTURE_ID.TOTAL_EXPORT_INCOME}.${yearField}`,
        value
      );
    }

    if (keyItem !== OTHER_INCOME_STRUCTURE_ID.OTHER_PRODUCT_INCOME.toString()) {
      calcProductIncomeOther(totalValue, yearField);
    }

    calcProductIncomePct(value, totalValue, yearField);
  };

  const calcProductIncomeOther = (
    totalValue: string,
    yearField: keyof IIncomeItemFormValues
  ) => {
    const extendedValues = sumExtendedValues(
      productIncomeItems,
      INCOME_SUBGROUP_CATEGORY.PRODUCT_INCOME,
      yearField
    );

    if (totalValue && extendedValues) {
      setValue(
        `incomeStructure.productIncomeItems.${OTHER_INCOME_STRUCTURE_ID.OTHER_PRODUCT_INCOME}.${yearField}`,
        calcNetIncomeItem(totalValue, extendedValues)
      );
    } else {
      setValue(
        `incomeStructure.productIncomeItems.${OTHER_INCOME_STRUCTURE_ID.OTHER_PRODUCT_INCOME}.${yearField}`,
        ''
      );
    }
  };

  const calcProductIncomePct = (
    value: string,
    totalValue: string,
    yearField: keyof IIncomeItemFormValues
  ) => {
    if (!totalValue) {
      Object.keys(productIncomePercentages).forEach((key) =>
        setValue(
          `incomeStructure.productIncomePercentages.${key}.${yearField}`,
          ''
        )
      );
      return;
    }

    if (!value) {
      setValue(
        `incomeStructure.productIncomePercentages.${keyItem}.${yearField}`,
        ''
      );
      return;
    }

    Object.entries(productIncomeItems).forEach(([key, item]) => {
      const itemValue = item[yearField] as string;

      if (itemValue) {
        setValue(
          `incomeStructure.productIncomePercentages.${key}.${yearField}`,
          calcIncomeItemPct(itemValue, totalValue)
        );
      }
    });
  };

  return (
    <Box
      width={1}
      display={'grid'}
      gridTemplateColumns={'repeat(3, 1fr)'}
      gap={3}
      ml={1}
    >
      {yearFields.map((yearField) => (
        <FormNumberInput
          key={`${keyItem}-${yearField}`}
          name={`incomeStructure.productIncomeItems.${keyItem}.${yearField}`}
          placeholder={'กรุณาระบุจำนวน'}
          slotProps={{
            input: {
              inputComponent: FormatNumberInput as React.ComponentType,
            },
          }}
          fullWidth
          sx={
            isCompletedTh || isCompletedEn ? disabledFormInputStyles : undefined
          }
          onChange={(event) =>
            handleChange(event as ChangeEvent<HTMLInputElement>, yearField)
          }
        />
      ))}
    </Box>
  );
};

export default ProductIncomeYearFieldsForm;
